import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HvLayer } from '../../components/common/HvLayer';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { Modal, Spinner } from '../../components/studio/studioUi';
import { errorText, videoService, type VideoRender } from '../../services/video.service';
import { PLATFORM_LABEL, socialService, type PostStatus, type ScheduledPost, type SocialAccount, type SocialConfig, type SocialPlatform } from '../../services/social.service';
import { ScheduleDialog } from '../video/ScheduleDialog';
import { AccountAvatar, toLocalInput } from '../video/socialUi';
import { FONT_SANS } from '../landing/landingData';
import { startBoot } from '../landing/BootOverlay';
import { EmptyBox, FillButton, OutlineButton, PageHead, PlusIcon, ProjectThumb, SearchBox, fmtAgo, fmtDur, useDash, useDismiss, type DashProject } from './dashUi';

/**
 * Dashboard Scheduler (design "HyVay Dashboard" → Scheduler): mini month + channel
 * filter on the left, a Day / Week / Month calendar of the user's social posts on
 * the right, a details sheet per post, and "Schedule a post" (pick a rendered
 * video here, then the existing ScheduleDialog does accounts, caption and time).
 */

// ---------------------------------------------------------------- date helpers (local time)
const HH = 48; // px per hour row
const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MS = MON.map((m) => m.slice(0, 3));
const DOWF = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addD = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const wd = (d: Date) => (d.getDay() + 6) % 7; // Monday = 0
const sow = (d: Date) => addD(d, -wd(d));
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** "GMT+7" from the browser's offset; the IANA name goes in the tooltip. */
const TZ_SHORT = (() => {
    const off = -new Date().getTimezoneOffset();
    const h = Math.trunc(Math.abs(off) / 60);
    const m = Math.abs(off) % 60;
    return `GMT${off >= 0 ? '+' : '−'}${h}${m ? `:${pad(m)}` : ''}`;
})();
const TZ_NAME = (() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
        return '';
    }
})();

const STATUS_WORD: Record<PostStatus, string> = { SCHEDULED: 'Scheduled', PUBLISHING: 'Posting…', PUBLISHED: 'Posted', FAILED: 'Failed', CANCELED: 'Cancelled' };
const ASPECT_OF: Record<string, string> = { portrait: '9:16', square: '1:1', landscape: '16:9' };

type CalView = 'day' | 'week' | 'month';
const VIEWS: Array<[CalView, string, string]> = [['day', 'Day', 'D'], ['week', 'Week', 'W'], ['month', 'Month', 'M']];

/** Posts of one video at the same minute are one chip ("3 accounts"), like the design. */
interface CalEvent {
    key: string;
    date: string;
    mins: number;
    at: Date;
    time: string;
    videoId: string;
    videoName: string;
    caption: string;
    members: ScheduledPost[];
    past: boolean;
    failed: boolean;
    sub: string;
}

const chipStyle = (e: CalEvent) => ({
    bg: e.failed ? 'var(--hv-card)' : 'var(--hv-mint)',
    fg: e.failed ? 'var(--hv-ink)' : 'var(--hv-mint-ink)',
    bd: e.failed ? '1.5px dashed var(--hv-danger)' : '0',
    dot: e.failed ? 'var(--hv-danger)' : 'var(--hv-brand-ink)',
    dotFill: e.failed ? 'transparent' : 'var(--hv-brand-ink)',
    opacity: e.past && !e.failed ? 0.5 : 1,
});

// ---------------------------------------------------------------- small local primitives
const ChevL = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
);
const ChevR = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
);
const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);
const roundBtn = (s: number): React.CSSProperties => ({ width: s, height: s, flex: 'none', borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' });
const linkBtn = (color: string): React.CSSProperties => ({ flex: 'none', whiteSpace: 'nowrap', padding: 0, border: 0, background: 'transparent', color, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', textUnderlineOffset: 3 });

/** Right-hand sheet from the design (post details, "Schedule a post"). Portaled so `position: fixed` is not caught by a transformed ancestor. */
const Sheet: React.FC<{ title: string; width: number; onClose: () => void; footer?: React.ReactNode; children: React.ReactNode }> = ({ title, width, onClose, footer, children }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <HvLayer>
            <style>{'@keyframes hvSchSlide{from{transform:translateX(32px);opacity:0}to{transform:none;opacity:1}}'}</style>
            <div style={{ position: 'fixed', inset: 0, zIndex: 90, fontFamily: FONT_SANS }}>
                <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(14,20,17,0.32)', animation: 'hvFade 0.18s ease both' }} />
                <aside role="dialog" aria-modal="true" aria-label={title} style={{ position: 'absolute', top: 8, right: 8, bottom: 8, width: `min(${width}px, calc(100% - 16px))`, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 30px 70px -24px rgba(0,0,0,0.35),0 8px 20px -12px rgba(0,0,0,0.18)', color: 'var(--hv-ink)', animation: 'hvSchSlide 0.32s cubic-bezier(0.32,0.72,0,1) both' }}>
                    <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px 8px 20px', background: 'var(--hv-page-2)', borderBottom: '1px solid var(--hv-line)' }}>
                        <div style={{ flex: 1, fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</div>
                        <button type="button" onClick={onClose} aria-label="Close" className="hv-iconbtn" style={{ ...roundBtn(36), color: 'var(--hv-muted)' }}><CloseIcon /></button>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 22 }}>{children}</div>
                    {footer && <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--hv-line)' }}>{footer}</div>}
                </aside>
            </div>
        </HvLayer>
    );
};

const Row: React.FC<{ label: string; top?: boolean; children: React.ReactNode }> = ({ label, top, children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)', gap: 12, alignItems: top ? 'start' : 'center', minHeight: 32, padding: top ? '6px 0' : 0 }}>
        <span style={{ fontSize: 12.5, color: 'var(--hv-body)' }}>{label}</span>
        <div style={{ fontSize: 13.5, minWidth: 0 }}>{children}</div>
    </div>
);

// ---------------------------------------------------------------- page
export const DashScheduler: React.FC = () => {
    const dash = useDash();
    const navigate = useNavigate();
    const confirm = useConfirm();

    const [cfg, setCfg] = useState<SocialConfig | null>(null);
    const [accounts, setAccounts] = useState<SocialAccount[] | null>(null);
    const [posts, setPosts] = useState<ScheduledPost[] | null>(null);
    const [now, setNow] = useState(() => new Date());

    const [view, setView] = useState<CalView>('week');
    const [cursor, setCursor] = useState(() => startOfDay(new Date()));
    const [mini, setMini] = useState<Date | null>(null);
    const [hidden, setHidden] = useState<Set<SocialPlatform>>(new Set());
    const [viewOpen, setViewOpen] = useState(false);
    const [selKey, setSelKey] = useState<string | null>(null);
    const [moving, setMoving] = useState<ScheduledPost[] | null>(null);
    const [picking, setPicking] = useState(false);
    const [posting, setPosting] = useState<{ video: DashProject; renders: VideoRender[] } | null>(null);
    const [renderCost, setRenderCost] = useState(10);

    const load = useCallback(() => {
        socialService.accounts().then(setAccounts).catch(() => setAccounts([]));
        socialService.posts().then(setPosts).catch(() => setPosts([]));
    }, []);
    useEffect(() => {
        socialService.config().then(setCfg).catch(() => setCfg(null));
        videoService.config().then((c) => setRenderCost(c.renderCost)).catch(() => {});
        load();
    }, [load]);

    // "Now" line + past/future styling.
    useEffect(() => {
        const t = window.setInterval(() => setNow(new Date()), 60_000);
        return () => window.clearInterval(t);
    }, []);

    // Refresh while something is going out (same rule as the old /video/schedule page).
    const busy = !!posts?.some((p) => p.status === 'PUBLISHING' || (p.status === 'SCHEDULED' && new Date(p.scheduledAt).getTime() < Date.now() + 60_000));
    useEffect(() => {
        if (!busy) return;
        const t = window.setInterval(() => socialService.posts().then(setPosts).catch(() => {}), 5000);
        return () => window.clearInterval(t);
    }, [busy]);

    // ------------------------------------------------ events
    const platforms = useMemo(() => {
        const seen: SocialPlatform[] = [];
        for (const a of accounts || []) if (!seen.includes(a.platform)) seen.push(a.platform);
        return seen;
    }, [accounts]);

    const events = useMemo<CalEvent[]>(() => {
        const groups = new Map<string, ScheduledPost[]>();
        for (const p of posts || []) {
            if (p.status === 'CANCELED' || hidden.has(p.platform)) continue;
            const at = new Date(p.scheduledAt);
            const k = `${iso(at)}|${hhmm(at)}|${p.videoId}`;
            groups.set(k, [...(groups.get(k) || []), p]);
        }
        return Array.from(groups.entries()).map(([key, ms]) => {
            const at = new Date(ms[0].scheduledAt);
            const failed = ms.some((m) => m.status === 'FAILED');
            const past = ms.every((m) => m.status === 'PUBLISHED') || at.getTime() < now.getTime();
            const channel = ms.length > 1 ? `${ms.length} accounts` : PLATFORM_LABEL[ms[0].platform] ?? ms[0].platform;
            return {
                key, at, date: iso(at), mins: at.getHours() * 60 + at.getMinutes(), time: hhmm(at),
                videoId: ms[0].videoId, videoName: ms[0].videoName || 'Video', caption: ms[0].caption,
                members: ms, past, failed,
                sub: `${hhmm(at)} · ${channel}${failed ? ' · Failed' : ''}`,
            };
        }).sort((a, b) => a.at.getTime() - b.at.getTime());
    }, [posts, hidden, now]);

    const byDate = useMemo(() => {
        const m = new Map<string, CalEvent[]>();
        for (const e of events) m.set(e.date, [...(m.get(e.date) || []), e]);
        return m;
    }, [events]);

    const sel = selKey ? events.find((e) => e.key === selKey) ?? null : null;
    useEffect(() => {
        if (selKey && posts && !sel) setSelKey(null); // its posts were cancelled / filtered out
    }, [selKey, posts, sel]);

    // ------------------------------------------------ navigation
    const todayIso = iso(now);
    const go = (v: CalView, d: Date) => { setView(v); setCursor(startOfDay(d)); setMini(null); };
    const step = (dir: number) => {
        const d = view === 'day' ? addD(cursor, dir) : view === 'week' ? addD(cursor, 7 * dir) : new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1);
        setCursor(d);
        setMini(null);
    };
    const toToday = () => { setCursor(startOfDay(new Date())); setMini(null); };

    // Keyboard: D / W / M switch the view, T jumps to today (design shortcuts).
    const overlayOpen = !!sel || picking || !!posting || !!moving;
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (overlayOpen || e.metaKey || e.ctrlKey || e.altKey) return;
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
            if (document.querySelector('[role="dialog"]')) return;
            const k = e.key.toLowerCase();
            const v = ({ d: 'day', w: 'week', m: 'month' } as Record<string, CalView>)[k];
            if (v) { setView(v); setViewOpen(false); }
            if (k === 't') toToday();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [overlayOpen]);

    const days = view === 'week' ? Array.from({ length: 7 }, (_, i) => addD(sow(cursor), i)) : [cursor];
    const label = (() => {
        if (view === 'month') return `${MON[cursor.getMonth()]} ${cursor.getFullYear()}`;
        if (view === 'day') return `${cursor.getDate()} ${MON[cursor.getMonth()]} ${cursor.getFullYear()}`;
        const a = days[0], b = days[6];
        if (a.getMonth() === b.getMonth()) return `${MON[a.getMonth()]} ${a.getFullYear()}`;
        return a.getFullYear() === b.getFullYear() ? `${MS[a.getMonth()]} – ${MS[b.getMonth()]} ${b.getFullYear()}` : `${MS[a.getMonth()]} ${a.getFullYear()} – ${MS[b.getMonth()]} ${b.getFullYear()}`;
    })();

    // Hour range: 08:00–20:00 by default, stretched to fit posts of the visible days.
    const wide = view === 'day';
    const hEv = wide ? 62 : 44;
    const visible = view === 'month' ? [] : days.flatMap((d) => byDate.get(iso(d)) || []);
    const startH = Math.min(8, ...visible.map((e) => Math.floor(e.mins / 60)));
    const endH = Math.min(24, Math.max(21, ...visible.map((e) => Math.ceil((e.mins + (hEv / HH) * 60) / 60))));
    const hours = Array.from({ length: endH - startH }, (_, i) => startH + i);
    const yOf = (mins: number) => ((mins - startH * 60) / 60) * HH;

    const scrollRef = useRef<HTMLDivElement>(null);
    const firstMins = visible.length ? Math.min(...visible.map((e) => e.mins)) : null;
    const rangeKey = `${view}|${iso(days[0])}`;
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop = firstMins !== null && firstMins < 8 * 60 ? Math.max(0, yOf(firstMins) - 12) : 0;
        // Only when the range changes, not on every poll.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeKey, posts === null]);

    const layoutDay = (list: CalEvent[]) => {
        const span = (hEv / HH) * 60;
        const groups: { items: CalEvent[]; end: number }[] = [];
        for (const e of list) {
            const g = groups[groups.length - 1];
            if (g && e.mins < g.end) { g.items.push(e); g.end = Math.max(g.end, e.mins + span); } else groups.push({ items: [e], end: e.mins + span });
        }
        return groups.flatMap((g) => {
            const n = g.items.length;
            return g.items.map((e, i) => ({ e, top: yOf(e.mins) + 1, left: `calc(${(i * 100) / n}% + 2px)`, width: `calc(${100 / n}% - ${n > 1 ? 4 : 8}px)`, z: i + 1 }));
        });
    };

    // ------------------------------------------------ actions
    const run = async (fn: () => Promise<unknown>, ok?: string) => {
        try {
            await fn();
            if (ok) dash.notify('success', ok);
        } catch (e) {
            dash.notify('error', errorText(e));
        } finally {
            load();
        }
    };
    const cancelEvent = async (e: CalEvent) => {
        const ids = e.members.filter((m) => m.status === 'SCHEDULED').map((m) => m.id);
        if (!ids.length) return;
        const ok = await confirm({
            title: <>Cancel <em>{e.videoName}</em>?</>,
            description: ids.length > 1 ? `It will not be posted to these ${ids.length} accounts. The video itself stays in your projects.` : 'It will not be posted. The video itself stays in your projects.',
            confirmLabel: 'Cancel post',
            cancelLabel: 'Keep it',
            busyLabel: 'Cancelling…',
            onConfirm: async () => { await Promise.all(ids.map((id) => socialService.cancel(id))); },
        });
        if (ok) { setSelKey(null); dash.notify('success', 'Post cancelled'); }
        load();
    };
    const removeFailed = (e: CalEvent) => run(() => Promise.all(e.members.filter((m) => m.status === 'FAILED').map((m) => socialService.cancel(m.id))), 'Removed from the calendar').then(() => setSelKey(null));

    const startSchedule = () => setPicking(true);
    const openFor = async (v: DashProject) => {
        try {
            const detail = await videoService.get(v.id);
            setPicking(false);
            setPosting({ video: v, renders: detail.renders });
        } catch (e) {
            dash.notify('error', errorText(e));
        }
    };
    // ScheduleDialog can queue a 9:16 render: follow it until it finishes.
    const postingId = posting?.video.id;
    const postingRender = !!posting?.renders.some((r) => r.status === 'QUEUED' || r.status === 'RENDERING');
    useEffect(() => {
        if (!postingId || !postingRender) return;
        const t = window.setInterval(() => {
            videoService.get(postingId).then((v) => setPosting((p) => (p && p.video.id === postingId ? { ...p, renders: v.renders } : p))).catch(() => {});
        }, 3000);
        return () => window.clearInterval(t);
    }, [postingId, postingRender]);

    const readyVideos = useMemo(() => dash.projects.filter((p) => p.kind === 'video' && !!p.renderUrl), [dash.projects]);
    const noAccounts = accounts !== null && accounts.length === 0;
    const noPosts = posts !== null && !(posts || []).some((p) => p.status !== 'CANCELED');

    // ------------------------------------------------ mini month
    const miniMonth = mini ?? new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const miniCells = (() => {
        const first = new Date(miniMonth.getFullYear(), miniMonth.getMonth(), 1);
        const n = Math.ceil((wd(first) + new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()) / 7) * 7;
        const wk0 = iso(sow(cursor)), wk6 = iso(addD(sow(cursor), 6));
        return Array.from({ length: n }, (_, i) => {
            const d = addD(sow(first), i);
            const k = iso(d);
            const inM = d.getMonth() === first.getMonth();
            const t = k === todayIso;
            const has = byDate.has(k);
            const selected = view === 'day' && k === iso(cursor) && !t;
            return { d, k, inM, t, has, selected, band: view === 'week' && k >= wk0 && k <= wk6 };
        });
    })();

    // ------------------------------------------------ render
    return (
        <section data-screen-label="Scheduler" style={{ display: 'flex', flexDirection: 'column', gap: 32, animation: 'hvFade 0.3s ease both' }}>
            <PageHead title="Scheduler" lead="Plan when your videos go out. They post automatically at the set time.">
                <FillButton icon={<PlusIcon />} onClick={startSchedule} disabled={cfg?.enabled === false}>Schedule a post</FillButton>
            </PageHead>

            {cfg && !cfg.enabled && <div style={{ marginTop: -16, fontSize: 13.5, color: 'var(--hv-danger)' }}>Scheduling is not available on this server yet.</div>}
            {noAccounts && (
                <EmptyBox
                    title="Connect a channel first"
                    text="Link YouTube, TikTok, Instagram, Facebook, LinkedIn or Threads, then schedule your rendered videos to post on their own."
                    action={<FillButton onClick={() => navigate('/dashboard/integration')}>Go to Integration</FillButton>}
                />
            )}
            {!noAccounts && accounts !== null && noPosts && (
                <EmptyBox
                    title="Nothing scheduled yet"
                    text={readyVideos.length ? 'Pick a rendered video and choose when it goes out. It shows up on the calendar below.' : 'Render a video first (open it in the editor and click Render MP4), then schedule it here.'}
                    action={readyVideos.length ? <OutlineButton onClick={startSchedule}><PlusIcon />Schedule a post</OutlineButton> : <OutlineButton onClick={() => navigate('/dashboard/projects?type=video')}>Open videos</OutlineButton>}
                />
            )}

            <div className="hv-cal-wrap" style={{ display: 'grid', gridTemplateColumns: '232px minmax(0,1fr)', gap: 28, alignItems: 'start', marginTop: -8 }}>
                {/* ---------------- side: mini month + channels */}
                <aside className="hv-cal-side" style={{ display: 'flex', flexDirection: 'column', gap: 26, position: 'sticky', top: 16 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 6 }}>
                            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700 }}>{MON[miniMonth.getMonth()]} {miniMonth.getFullYear()}</span>
                            <button type="button" aria-label="Previous month" className="hv-iconbtn" onClick={() => setMini(new Date(miniMonth.getFullYear(), miniMonth.getMonth() - 1, 1))} style={roundBtn(28)}><ChevL /></button>
                            <button type="button" aria-label="Next month" className="hv-iconbtn" onClick={() => setMini(new Date(miniMonth.getFullYear(), miniMonth.getMonth() + 1, 1))} style={roundBtn(28)}><ChevR /></button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', rowGap: 2, marginTop: 8 }}>
                            {DOWF.map((w) => (
                                <span key={w} style={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 600, color: 'var(--hv-muted)' }}>{w[0]}</span>
                            ))}
                            {miniCells.map((c) => (
                                <div key={c.k} style={{ display: 'flex', justifyContent: 'center', background: c.band ? 'var(--hv-page-2)' : 'transparent', borderRadius: wd(c.d) === 0 ? '999px 0 0 999px' : wd(c.d) === 6 ? '0 999px 999px 0' : 0 }}>
                                    <button
                                        type="button"
                                        aria-label={`${c.d.getDate()} ${MON[c.d.getMonth()]}${c.has ? ', has posts' : ''}`}
                                        onClick={() => go(view === 'month' ? 'day' : view, c.d)}
                                        className={c.t ? undefined : 'hv-iconbtn'}
                                        style={{ position: 'relative', width: 28, height: 28, borderRadius: 999, border: 0, background: c.t ? 'var(--brand,#13422C)' : c.selected ? 'var(--hv-mint)' : 'transparent', color: c.t ? '#FFFDF7' : c.inM ? 'var(--hv-ink)' : 'var(--hv-muted)', fontSize: 11.5, fontWeight: c.t || c.has || c.selected ? 700 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        {c.d.getDate()}
                                        {c.has && !c.t && <span style={{ position: 'absolute', bottom: 3, left: '50%', width: 3, height: 3, marginLeft: -1.5, borderRadius: 999, background: c.inM ? 'var(--hv-brand-ink)' : 'var(--hv-muted)' }} />}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div style={{ paddingLeft: 6, fontSize: 13.5, fontWeight: 700 }}>Channels</div>
                        {accounts === null && <div style={{ padding: '10px 6px' }}><Spinner size={14} /></div>}
                        {noAccounts && (
                            <div style={{ marginTop: 8, padding: '0 6px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--hv-body)' }}>
                                No channels connected yet.{' '}
                                <button type="button" onClick={() => navigate('/dashboard/integration')} style={{ padding: 0, border: 0, background: 'transparent', font: 'inherit', color: 'var(--hv-brand-ink)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, textDecorationThickness: 1 }}>Connect an account</button>
                            </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: 8 }}>
                            {platforms.map((p) => {
                                const shown = !hidden.has(p);
                                const c = 'var(--brand,#13422C)';
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        role="checkbox"
                                        aria-checked={shown}
                                        className="hv-iconbtn"
                                        onClick={() => setHidden((h) => { const n = new Set(h); if (n.has(p)) n.delete(p); else n.add(p); return n; })}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, height: 34, padding: '0 6px', border: 0, borderRadius: 8, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
                                    >
                                        <span style={{ flex: 'none', width: 16, height: 16, borderRadius: 4, border: `2px solid ${c}`, background: shown ? c : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
                                        </span>
                                        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{PLATFORM_LABEL[p] ?? p}</span>
                                    </button>
                                );
                            })}
                            {platforms.length > 0 && (
                                <button type="button" onClick={() => navigate('/dashboard/integration')} className="hv-iconbtn" style={{ display: 'flex', alignItems: 'center', gap: 10, height: 34, padding: '0 6px', border: 0, borderRadius: 8, background: 'transparent', color: 'var(--hv-muted)', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flex: 'none' }} aria-hidden="true"><path d="M12 6v12M6 12h12" /></svg>
                                    <span>Add channel</span>
                                </button>
                            )}
                        </div>
                    </div>
                </aside>

                {/* ---------------- main calendar */}
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <OutlineButton onClick={toToday} style={{ background: 'transparent' }}>Today</OutlineButton>
                        <button type="button" aria-label="Previous" className="hv-iconbtn" onClick={() => step(-1)} style={roundBtn(34)}><ChevL size={15} /></button>
                        <button type="button" aria-label="Next" className="hv-iconbtn" onClick={() => step(1)} style={roundBtn(34)}><ChevR size={15} /></button>
                        <div style={{ marginLeft: 6, fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>{label}</div>
                        {posts === null && <Spinner size={14} />}
                        <div style={{ flex: 1 }} />
                        <ViewPicker value={view} open={viewOpen} setOpen={setViewOpen} onChange={(v) => { setView(v); setViewOpen(false); }} />
                    </div>

                    {view !== 'month' && (
                        <div style={{ border: '1px solid var(--hv-line)', borderRadius: 8, background: 'var(--hv-card)', overflow: 'hidden' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(${days.length},minmax(0,1fr))`, overflowY: 'hidden', scrollbarGutter: 'stable', borderBottom: '1px solid var(--hv-line)' }}>
                                <div title={TZ_NAME || undefined} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: '0 8px 6px 0', fontSize: 9.5, color: 'var(--hv-muted)' }}>{TZ_SHORT}</div>
                                {days.map((d) => {
                                    const t = iso(d) === todayIso;
                                    return (
                                        <button key={iso(d)} type="button" onClick={() => go('day', d)} aria-label={`${DOWF[wd(d)]} ${d.getDate()} ${MON[d.getMonth()]}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '8px 0 6px', border: 0, background: 'transparent', cursor: 'pointer', minWidth: 0 }}>
                                            <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', color: t ? 'var(--hv-brand-ink)' : 'var(--hv-muted)' }}>{DOWF[wd(d)].slice(0, 3).toUpperCase()}</span>
                                            <span className={t ? undefined : 'hv-iconbtn'} style={{ width: 32, height: 32, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t ? 'var(--brand,#13422C)' : 'transparent', color: t ? '#FFFDF7' : 'var(--hv-ink)', fontSize: 17, fontWeight: 500 }}>{d.getDate()}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div ref={scrollRef} style={{ height: 'min(560px, calc(100vh - 300px))', minHeight: 320, overflowY: 'auto', overscrollBehavior: 'contain', scrollbarGutter: 'stable' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(${days.length},minmax(0,1fr))`, height: hours.length * HH + 8, paddingTop: 8 }}>
                                    <div>
                                        {hours.map((h) => (
                                            <div key={h} style={{ position: 'relative', height: HH }}>
                                                <span style={{ position: 'absolute', right: 8, top: -7, fontSize: 10.5, color: 'var(--hv-muted)', fontVariantNumeric: 'tabular-nums' }}>{pad(h)}:00</span>
                                            </div>
                                        ))}
                                    </div>
                                    {days.map((d) => {
                                        const k = iso(d);
                                        const nowMins = now.getHours() * 60 + now.getMinutes();
                                        const showNow = k === todayIso && nowMins >= startH * 60 && nowMins <= endH * 60;
                                        return (
                                            <div key={k} style={{ position: 'relative', minWidth: 0, borderLeft: '1px solid var(--hv-line)', backgroundImage: `repeating-linear-gradient(to bottom,var(--hv-line) 0,var(--hv-line) 1px,transparent 1px,transparent ${HH}px)` }}>
                                                {layoutDay(byDate.get(k) || []).map(({ e, top, left, width, z }) => {
                                                    const s = chipStyle(e);
                                                    return (
                                                        <button
                                                            key={e.key}
                                                            type="button"
                                                            onClick={(ev) => { ev.stopPropagation(); setSelKey(e.key); }}
                                                            title={`${e.videoName} · ${e.sub}`}
                                                            className="hv-sch-chip"
                                                            style={{ position: 'absolute', left, width, zIndex: z, top, height: hEv, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, padding: '4px 8px', borderRadius: 6, border: s.bd, outline: '1.5px solid var(--hv-card)', background: s.bg, color: s.fg, opacity: s.opacity, textAlign: 'left', cursor: 'pointer', overflow: 'hidden' }}
                                                        >
                                                            <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, fontWeight: 700 }}>{e.videoName}</span>
                                                            <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, opacity: 0.9 }}>{e.sub}</span>
                                                            {wide && <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, opacity: 0.9 }}>{e.caption}</span>}
                                                        </button>
                                                    );
                                                })}
                                                {showNow && (
                                                    <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: yOf(nowMins), height: 2, background: '#D93025', pointerEvents: 'none', zIndex: 20 }}>
                                                        <span style={{ position: 'absolute', left: -6, top: -5, width: 12, height: 12, borderRadius: 999, background: '#D93025' }} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {view === 'month' && (
                        <MonthGrid cursor={cursor} todayIso={todayIso} byDate={byDate} onPickDay={(d) => go('day', d)} onOpen={(e) => setSelKey(e.key)} />
                    )}
                </div>
            </div>
            <style>{'.hv-dash .hv-sch-chip{transition:box-shadow .15s ease}.hv-dash .hv-sch-chip:hover{box-shadow:0 4px 12px -4px rgba(0,0,0,0.35)}'}</style>

            {sel && (
                <PostSheet
                    e={sel}
                    project={dash.projects.find((p) => p.id === sel.videoId) ?? null}
                    onClose={() => setSelKey(null)}
                    onOpenVideo={() => { startBoot(`OPENING ${sel.videoName.toUpperCase()}`, 'video'); navigate(`/video/${sel.videoId}`); }}
                    onCancel={() => void cancelEvent(sel)}
                    onEdit={() => { setMoving(sel.members.filter((m) => m.status === 'SCHEDULED')); setSelKey(null); }}
                    onRetry={(p) => void run(() => socialService.retry(p.id), 'Retrying now')}
                    onRemoveFailed={() => void removeFailed(sel)}
                />
            )}

            {picking && <VideoPicker videos={readyVideos} loading={dash.projectsLoading} onClose={() => setPicking(false)} onPick={openFor} onOpenProjects={() => navigate('/dashboard/projects?type=video')} />}

            {posting && (
                <HvLayer>
                    <ScheduleDialog
                        open
                        onClose={() => setPosting(null)}
                        videoName={posting.video.name}
                        renders={posting.renders}
                        renderCost={renderCost}
                        onRenderPortrait={() => {
                            const id = posting.video.id;
                            videoService
                                .render(id, 'portrait')
                                .then((r) => {
                                    setPosting((p) => (p && p.video.id === id ? { ...p, renders: [r, ...p.renders] } : p));
                                    dash.notify('success', '9:16 render started.');
                                    dash.refreshCredits();
                                })
                                .catch((e) => dash.notify('error', errorText(e)));
                        }}
                        onScheduled={() => {
                            load();
                            toToday();
                        }}
                    />
                </HvLayer>
            )}

            {moving && moving.length > 0 && (
                <RescheduleModal posts={moving} maxAheadDays={cfg?.maxAheadDays ?? 90} onClose={() => setMoving(null)} onSaved={(d) => { load(); go(view, d); dash.notify('success', `Rescheduled for ${d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`); }} />
            )}
        </section>
    );
};

// ---------------------------------------------------------------- view dropdown
const ViewPicker: React.FC<{ value: CalView; open: boolean; setOpen: (o: boolean) => void; onChange: (v: CalView) => void }> = ({ value, open, setOpen, onChange }) => {
    const close = useCallback(() => setOpen(false), [setOpen]);
    const ref = useDismiss<HTMLDivElement>(open, close);
    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)} className="hv-btn-out" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 10px 0 14px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {VIEWS.find((v) => v[0] === value)?.[1]}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {open && (
                <div role="listbox" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 30, width: 170, padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -18px var(--hv-shadow)', animation: 'hvPop 0.18s ease both' }}>
                    {VIEWS.map(([k, l, key]) => (
                        <button key={k} type="button" role="option" aria-selected={k === value} onClick={() => onChange(k)} className="hv-mi" style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '8px 10px', border: 0, borderRadius: 5, background: k === value ? 'var(--hv-page-2)' : 'transparent', fontSize: 13, fontWeight: 500, color: 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left' }}>
                            <span style={{ flex: 1 }}>{l}</span>
                            <span style={{ fontFamily: FONT_SANS, fontSize: 10.5, color: 'var(--hv-muted)' }}>{key}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------- month view
const MonthGrid: React.FC<{ cursor: Date; todayIso: string; byDate: Map<string, CalEvent[]>; onPickDay: (d: Date) => void; onOpen: (e: CalEvent) => void }> = ({ cursor, todayIso, byDate, onPickDay, onOpen }) => {
    const st = sow(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    const all = Array.from({ length: 42 }, (_, i) => addD(st, i));
    const cells = all.slice(35).some((d) => d.getMonth() === cursor.getMonth()) ? all : all.slice(0, 35);
    return (
        <div style={{ border: '1px solid var(--hv-line)', borderRadius: 8, background: 'var(--hv-card)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))' }}>
                {DOWF.map((w, i) => (
                    <div key={w} style={{ padding: '10px 0 4px', textAlign: 'center', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--hv-muted)', borderLeft: i ? '1px solid var(--hv-line)' : 0 }}>{w.slice(0, 3).toUpperCase()}</div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))' }}>
                {cells.map((d, i) => {
                    const k = iso(d);
                    const list = byDate.get(k) || [];
                    const inM = d.getMonth() === cursor.getMonth();
                    const t = k === todayIso;
                    return (
                        <div key={k} role="button" tabIndex={0} onClick={() => onPickDay(d)} onKeyDown={(ev) => { if (ev.key === 'Enter') onPickDay(d); }} style={{ minWidth: 0, minHeight: 112, padding: '4px 4px 6px', borderLeft: i % 7 ? '1px solid var(--hv-line)' : 0, borderTop: '1px solid var(--hv-line)', display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer' }}>
                            <span style={{ alignSelf: 'center', minWidth: 26, height: 26, padding: '0 7px', boxSizing: 'border-box', whiteSpace: 'nowrap', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, background: t ? 'var(--brand,#13422C)' : 'transparent', color: t ? '#FFFDF7' : inM ? 'var(--hv-ink)' : 'var(--hv-muted)' }}>
                                {d.getDate() === 1 ? `${d.getDate()} ${MS[d.getMonth()]}` : d.getDate()}
                            </span>
                            {list.slice(0, 3).map((e) => {
                                const s = chipStyle(e);
                                return (
                                    <button key={e.key} type="button" title={`${e.videoName} · ${e.sub}`} onClick={(ev) => { ev.stopPropagation(); onOpen(e); }} className="hv-iconbtn" style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0, height: 22, padding: '0 6px', border: 0, borderRadius: 6, background: 'transparent', opacity: s.opacity, fontSize: 11.5, color: 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left' }}>
                                        <span style={{ flex: 'none', width: 8, height: 8, borderRadius: 999, background: s.dotFill, boxShadow: `inset 0 0 0 1.5px ${s.dot}` }} />
                                        <span style={{ flex: 'none', color: 'var(--hv-body)' }}>{e.time}</span>
                                        <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>{e.videoName}</span>
                                    </button>
                                );
                            })}
                            {list.length > 3 && <span style={{ padding: '0 6px', fontSize: 11.5, fontWeight: 600, color: 'var(--hv-body)' }}>{list.length - 3} more</span>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------- post details sheet
const PostSheet: React.FC<{
    e: CalEvent;
    project: DashProject | null;
    onClose: () => void;
    onOpenVideo: () => void;
    onCancel: () => void;
    onEdit: () => void;
    onRetry: (p: ScheduledPost) => void;
    onRemoveFailed: () => void;
}> = ({ e, project, onClose, onOpenVideo, onCancel, onEdit, onRetry, onRemoveFailed }) => {
    const ms = e.members;
    const canCancel = ms.some((m) => m.status === 'SCHEDULED');
    const failed = ms.filter((m) => m.status === 'FAILED');
    const statuses = Array.from(new Set(ms.map((m) => STATUS_WORD[m.status])));
    const format = ms[0].format;
    const d = e.at;
    const footer = canCancel ? (
        <>
            <button type="button" onClick={onCancel} className="hv-link" style={linkBtn('var(--hv-danger)')}>Cancel post</button>
            <div style={{ flex: 1 }} />
            <OutlineButton onClick={onEdit} style={{ height: 36, padding: '0 18px', background: 'transparent', fontWeight: 700, fontSize: 13.5 }}>Edit schedule</OutlineButton>
        </>
    ) : failed.length ? (
        <>
            <button type="button" onClick={onRemoveFailed} className="hv-link" style={linkBtn('var(--hv-danger)')}>Remove from calendar</button>
            <div style={{ flex: 1 }} />
            {failed.length === 1 && <OutlineButton onClick={() => onRetry(failed[0])} style={{ height: 36, padding: '0 18px', background: 'transparent', fontWeight: 700, fontSize: 13.5 }}>Retry</OutlineButton>}
        </>
    ) : undefined;
    return (
        <Sheet title={e.past && !canCancel ? 'Post' : 'Scheduled post'} width={440} onClose={onClose} footer={footer}>
            <div style={{ borderRadius: 12, overflow: 'hidden' }}>
                {project ? (
                    <ProjectThumb p={{ ...project, aspect: (format && (ASPECT_OF[format] as DashProject['aspect'])) || project.aspect }} height={220} radius="12px" />
                ) : (
                    <div style={{ height: 220, borderRadius: 12, background: 'var(--hv-page-2)' }} />
                )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Row label="Name">{e.videoName}</Row>
                <Row label="Status">{statuses.join(' / ')}</Row>
                {format && <Row label="Aspect ratio">{ASPECT_OF[format] ?? format}</Row>}
                {project?.durationSec ? <Row label="Duration"><span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDur(project.durationSec)}</span></Row> : null}
                <Row label="Date">{DOWF[wd(d)]}, {d.getDate()} {MON[d.getMonth()]} {d.getFullYear()}</Row>
                <Row label="Time"><span style={{ fontVariantNumeric: 'tabular-nums' }}>{e.time} ({TZ_SHORT})</span></Row>
                <Row label={ms.length > 1 ? 'Channels' : 'Channel'} top>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                        {ms.map((m) => (
                            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                    <AccountAvatar account={m.account} size={22} />
                                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.account.name}</span>
                                        <span style={{ fontSize: 12, color: 'var(--hv-body)' }}>
                                            {PLATFORM_LABEL[m.platform] ?? m.platform}{m.options?.placement === 'story' ? ' Story' : ''}
                                            {ms.length > 1 && <> · <span style={{ color: m.status === 'FAILED' ? 'var(--hv-danger)' : 'inherit' }}>{STATUS_WORD[m.status]}</span></>}
                                        </span>
                                    </span>
                                    {m.status === 'FAILED' && ms.length > 1 && (
                                        <button type="button" onClick={() => onRetry(m)} className="hv-link" style={{ ...linkBtn('var(--hv-brand-ink)'), fontSize: 12.5 }}>Retry</button>
                                    )}
                                </span>
                                {m.status === 'FAILED' && m.error && <span style={{ paddingLeft: 32, fontSize: 12, lineHeight: 1.5, color: 'var(--hv-danger)', overflowWrap: 'anywhere' }}>{m.error}</span>}
                                {m.status === 'PUBLISHED' && m.publishedAt && <span style={{ paddingLeft: 32, fontSize: 12, color: 'var(--hv-muted)' }}>Posted {fmtAgo(m.publishedAt).toLowerCase()}</span>}
                                {m.externalUrl && (
                                    <a href={m.externalUrl} target="_blank" rel="noopener noreferrer" className="hv-link" style={{ paddingLeft: 32, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--hv-brand-ink)', textDecoration: 'none' }}>
                                        View post
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4h6v6M20 4l-9 9M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" /></svg>
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                </Row>
                {e.caption && (
                    <Row label="Caption" top>
                        <span style={{ lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{e.caption}</span>
                    </Row>
                )}
            </div>
            <button type="button" onClick={onOpenVideo} className="hv-link" style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, padding: 0, border: 0, background: 'transparent', color: 'var(--hv-brand-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer', textUnderlineOffset: 3 }}>
                Open video in editor
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </button>
        </Sheet>
    );
};

// ---------------------------------------------------------------- "Schedule a post" step 1: pick a rendered video
const VideoPicker: React.FC<{ videos: DashProject[]; loading: boolean; onClose: () => void; onPick: (v: DashProject) => Promise<void>; onOpenProjects: () => void }> = ({ videos, loading, onClose, onPick, onOpenProjects }) => {
    const [q, setQ] = useState('');
    const [selId, setSelId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const list = videos.filter((v) => !q.trim() || v.name.toLowerCase().includes(q.trim().toLowerCase()));
    const chosen = videos.find((v) => v.id === selId) ?? null;
    const next = async () => {
        if (!chosen) return;
        setBusy(true);
        try { await onPick(chosen); } finally { setBusy(false); }
    };
    const cols = '16px minmax(0,1.6fr) minmax(56px,0.7fr) minmax(52px,0.6fr) minmax(96px,1fr)';
    return (
        <Sheet
            title="Schedule a post"
            width={680}
            onClose={onClose}
            footer={
                <>
                    <button type="button" onClick={onClose} className="hv-iconbtn" style={{ height: 36, padding: '0 14px', borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13.5, cursor: 'pointer' }}>Cancel</button>
                    <div style={{ flex: 1, fontSize: 12.5, color: 'var(--hv-muted)', textAlign: 'right' }}>Next: channels, caption and time</div>
                    <FillButton onClick={() => void next()} disabled={!chosen || busy}>{busy ? <Spinner light size={14} /> : 'Continue'}</FillButton>
                </>
            }
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>Choose video</div>
                <SearchBox value={q} onChange={setQ} placeholder="Search videos" />
            </div>
            {loading && !videos.length && <Spinner />}
            {!loading && !videos.length && (
                <EmptyBox title="No rendered videos yet" text="Only videos with a finished MP4 render can be posted. Open a video, click Render MP4, then come back." action={<OutlineButton onClick={onOpenProjects}>Open videos</OutlineButton>} />
            )}
            {videos.length > 0 && !list.length && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '40px 8px', textAlign: 'center', fontSize: 12.5, color: 'var(--hv-muted)' }}>
                    <span>No videos match “{q}”</span>
                    <span>Check the spelling or try another keyword.</span>
                </div>
            )}
            {list.length > 0 && (
                <div role="listbox" aria-label="Videos" style={{ marginTop: -8 }}>
                    <div style={{ display: 'grid', columnGap: 16, alignItems: 'center', padding: '0 12px', height: 36, background: 'var(--hv-page-2)', borderTop: '1px solid var(--hv-line)', borderBottom: '1px solid var(--hv-line)', fontSize: 12, fontWeight: 600, color: 'var(--hv-body)', gridTemplateColumns: cols }}>
                        <span /><span style={{ marginLeft: -6 }}>Video</span><span>Ratio</span><span>Length</span><span>Last updated</span>
                    </div>
                    {list.map((v) => {
                        const on = v.id === selId;
                        return (
                            <button key={v.id} type="button" role="option" aria-selected={on} onClick={() => setSelId(v.id)} onDoubleClick={() => { setSelId(v.id); void onPick(v); }} className="hv-vp-row" style={{ display: 'grid', columnGap: 16, alignItems: 'center', width: '100%', minHeight: 44, padding: '8px 12px', border: 0, borderBottom: '1px solid var(--hv-line)', background: on ? 'color-mix(in srgb,var(--hv-mint) 45%,var(--hv-card))' : 'transparent', color: 'var(--hv-ink)', fontSize: 13, textAlign: 'left', cursor: 'pointer', gridTemplateColumns: cols }}>
                                <span style={{ width: 16, height: 16, borderRadius: 999, border: `1.5px solid ${on ? 'var(--brand,#13422C)' : 'var(--hv-line-2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {on && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--brand,#13422C)' }} />}
                                </span>
                                <span className="hv-rowname" style={{ marginLeft: -6, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</span>
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v.aspect ?? '–'}</span>
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDur(v.durationSec) || '–'}</span>
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmtAgo(v.updatedAt)}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </Sheet>
    );
};

// ---------------------------------------------------------------- edit schedule (all scheduled posts of one chip)
const RescheduleModal: React.FC<{ posts: ScheduledPost[]; maxAheadDays: number; onClose: () => void; onSaved: (d: Date) => void }> = ({ posts, maxAheadDays, onClose, onSaved }) => {
    const dash = useDash();
    const [at, setAt] = useState(() => toLocalInput(new Date(posts[0].scheduledAt)));
    const [busy, setBusy] = useState(false);
    const valid = !!at && new Date(at).getTime() > Date.now();
    const save = async () => {
        if (!valid) return;
        setBusy(true);
        try {
            const when = new Date(at);
            await Promise.all(posts.map((p) => socialService.update(p.id, { scheduledAt: when.toISOString() })));
            onSaved(when);
            onClose();
        } catch (e) {
            dash.notify('error', errorText(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <HvLayer>
            <Modal open onClose={onClose} maxWidth={420} label="Edit schedule">
                <div style={{ padding: 22, display: 'grid', gap: 14, fontFamily: FONT_SANS }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, fontSize: 16, fontWeight: 700 }}>Edit schedule</div>
                        <button type="button" onClick={onClose} aria-label="Close" className="hv-iconbtn" style={{ ...roundBtn(34), color: 'var(--hv-muted)' }}><CloseIcon /></button>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--hv-body)' }}>
                        {posts[0].videoName}{posts.length > 1 ? ` · ${posts.length} accounts` : ` · ${PLATFORM_LABEL[posts[0].platform] ?? posts[0].platform}`}
                    </div>
                    <input
                        type="datetime-local"
                        value={at}
                        min={toLocalInput(new Date())}
                        max={toLocalInput(new Date(Date.now() + maxAheadDays * 86400_000))}
                        onChange={(e) => setAt(e.target.value)}
                        style={{ height: 40, padding: '0 12px', borderRadius: 10, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 14, fontFamily: 'inherit' }}
                    />
                    <div style={{ fontSize: 12, color: 'var(--hv-muted)' }}>Time zone: {TZ_NAME ? `${TZ_NAME} (${TZ_SHORT})` : TZ_SHORT}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <OutlineButton onClick={onClose}>Cancel</OutlineButton>
                        <FillButton onClick={() => void save()} disabled={!valid || busy}>{busy ? <Spinner light size={14} /> : 'Save'}</FillButton>
                    </div>
                </div>
            </Modal>
        </HvLayer>
    );
};

export default DashScheduler;
